import { RandomInteger } from "./RandomInteger"

/**
 * Randomly selects items from array.
 *
 * Uses RandomInteger to select indices. Same seed produces same choices.
 * If no seed provided, uses random seed.
 *
 * @param n - Number of items to select
 * @param choices - Array of items to choose from
 * @param seed - Random seed for reproducibility (optional, random if not provided)
 * @returns Iterator yielding randomly selected items
 *
 * @example
 * ```ts
 * import { RandomChoice } from "@pdfking/tfunc"
 *
 * const suits = ["♠", "♥", "♦", "♣"]
 * Array.from(RandomChoice(5, suits, 42))
 * // ["♥", "♠", "♦", "♠", "♥"]
 *
 * Array.from(RandomChoice(5, suits)) // truly random
 * ```
 */
export const RandomChoice = function* <T>(
  n: number,
  choices: T[],
  seed?: number
): IterableIterator<T> {
  // Generate random indices in the range [0, choices.length - 1]
  const indices = RandomInteger(n, 0, choices.length - 1, seed)

  // Map each random index to the corresponding choice
  for (const index of indices) {
    yield choices[index]
  }
}
